import { DeleteOutlineOutlined } from "@mui/icons-material";
import clsx from "clsx";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { components, OptionProps } from "react-select";

import { PIC, SVG_MUI } from "@ryde/assets";
import {
  IDropdownItem,
  onActivateKeyDown,
  useViewScopePermission,
} from "@ryde/shared";

import { FramerTooltip } from "../../common";
import { MuiIconButton } from "../../controls";
import {
  ViewDropdownIconsContainer,
  ViewDropdownLabelContainer,
  ViewDropdownOptionStyled,
  ViewOptionContainer,
} from "./view.styled";

interface ICustomOptionProps extends OptionProps {
  defaultViewId: number | undefined;
  activeViewId: number | undefined;
  handleUpdate: (viewId: number) => Promise<void>;
  handleDelete: (viewId: number) => Promise<void>;
  editViewHandler: (viewId: number) => void;
}

export const ViewDropdownOption: FC<ICustomOptionProps> = props => {
  const {
    data,
    handleUpdate,
    handleDelete,
    editViewHandler,
    activeViewId,
    defaultViewId,
  } = props;

  const { t } = useTranslation();
  const { isSaveButtonDisabled } = useViewScopePermission();

  const view = data as IDropdownItem;
  const isActiveView = useMemo(
    () => Boolean(activeViewId && String(activeViewId) === String(view.value)),
    [activeViewId, view.value],
  );
  const isDefaultView = useMemo(
    () =>
      Boolean(defaultViewId && String(defaultViewId) === String(view.value)),
    [defaultViewId, view.value],
  );

  return (
    <ViewDropdownOptionStyled
      data-e2e-id={view.value}
      className={clsx({
        ViewDropdownOption_Default: isDefaultView,
        ViewDropdownOption_Active: isActiveView,
      })}
    >
      <components.Option {...props} className="ViewDropdownOption-Option">
        <ViewOptionContainer>
          <ViewDropdownLabelContainer className="truncate-text">
            {props.label}
          </ViewDropdownLabelContainer>

          <ViewDropdownIconsContainer>
            <span className="ViewDropdownOption-IconWrapper ViewDropdownOption-IconWrapper_Delete">
              <FramerTooltip content={t("view.delete")}>
                <MuiIconButton
                  size="small"
                  onClick={event => {
                    event.stopPropagation();
                    handleDelete(Number(view.value));
                  }}
                >
                  <DeleteOutlineOutlined />
                </MuiIconButton>
              </FramerTooltip>
            </span>

            <span className="ViewDropdownOption-IconWrapper ViewDropdownOption-IconWrapper_Update">
              <FramerTooltip content={t("view.update")}>
                <MuiIconButton
                  size="small"
                  onClick={event => {
                    event.stopPropagation();
                    handleUpdate(Number(view.value));
                  }}
                >
                  <PIC.SaveSVG role="button" />
                </MuiIconButton>
              </FramerTooltip>
            </span>
            <span
              className="ViewDropdownOption-IconWrapper ViewDropdownOption-IconWrapper_Scope"
              onClick={event => {
                event.stopPropagation();
                editViewHandler(Number(view.value));
              }}
              onKeyDown={onActivateKeyDown(event => {
                event.stopPropagation();
                editViewHandler(Number(view.value));
              })}
              role="button"
              tabIndex={0}
              aria-label={t("view.update")}
            >
              <span>
                {isDefaultView ? <PIC.StarFilledSVG /> : <PIC.StarSVG />}
              </span>
            </span>
            {!isSaveButtonDisabled && (
              <span className="ViewDropdownOption-IconWrapper ViewDropdownOption-IconWrapper_EditView">
                <FramerTooltip content={t("view.update")}>
                  <MuiIconButton
                    size="small"
                    onClick={event => {
                      event.stopPropagation();
                      editViewHandler(Number(view.value));
                    }}
                  >
                    <SVG_MUI.EllipsisVertical3SVG />
                  </MuiIconButton>
                </FramerTooltip>
              </span>
            )}
          </ViewDropdownIconsContainer>
        </ViewOptionContainer>
      </components.Option>
    </ViewDropdownOptionStyled>
  );
};
